import React, { useState, useRef, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';

function ChatBubble({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex items-end gap-2 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      {!isUser && (
        <div className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0 mb-0.5">
          <svg className="w-3 h-3 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </div>
      )}
      <div className={`max-w-[82%] rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words ${
        isUser
          ? 'bg-violet-600 text-white rounded-br-sm'
          : 'bg-white border border-violet-100 text-gray-800 rounded-bl-sm shadow-sm'
      }`}>
        {msg.text}
      </div>
    </div>
  );
}

const SUGGESTIONS = [
  '¿Qué refrigerante usa la Mitsubishi Ecodan?',
  '¿Presión normal en un sistema de aerotermia?',
  '¿Cómo se purga un suelo radiante?',
];

const BTN_BOTTOM = 24;   // bottom-6
const BTN_RIGHT  = 24;   // right-6
const BTN_SIZE   = 56;   // h-14
const PANEL_BOTTOM = BTN_BOTTOM + BTN_SIZE + 8;

export default function FloatingChatIA() {
  const [isOpen, setIsOpen]     = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [threadId, setThreadId] = useState(null);
  const [error, setError]       = useState('');
  const [unread, setUnread]     = useState(false);

  const bottomRef = useRef(null);
  const inputRef  = useRef(null);
  const panelRef  = useRef(null);

  // Scroll al final cuando llega un mensaje nuevo
  useEffect(() => {
    if (isOpen) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading, isOpen]);

  // Al abrir: limpiar no-leídos y enfocar input
  useEffect(() => {
    if (isOpen) {
      setUnread(false);
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  // Cuando el teclado virtual sube, reubicar el panel sobre él
  useEffect(() => {
    if (!isOpen) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const onResize = () => {
      if (!panelRef.current) return;
      const keyboardHeight = window.innerHeight - vv.height;
      panelRef.current.style.bottom = keyboardHeight > 100
        ? `${keyboardHeight + 8}px`
        : `${PANEL_BOTTOM}px`;
    };
    vv.addEventListener('resize', onResize);
    return () => vv.removeEventListener('resize', onResize);
  }, [isOpen]);

  const send = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');
    setError('');
    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setLoading(true);
    try {
      const resp = await fetch('/.netlify/functions/chatWithAssistant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ threadId, message: msg }),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Error del asistente');
      if (data.threadId && !threadId) setThreadId(data.threadId);
      setMessages(prev => [...prev, { role: 'assistant', text: data.response }]);
      if (!isOpen) setUnread(true);
    } catch {
      setError('Error al conectar. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  }, [input, loading, threadId, isOpen]);

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const reset = () => { setMessages([]); setThreadId(null); setInput(''); setError(''); };

  const btnStyle = {
    bottom: `calc(${BTN_BOTTOM}px + env(safe-area-inset-bottom, 0px))`,
    right:  `calc(${BTN_RIGHT}px + env(safe-area-inset-right, 0px))`,
  };

  const panelStyle = {
    bottom: `calc(${PANEL_BOTTOM}px + env(safe-area-inset-bottom, 0px))`,
    right:  `calc(${BTN_RIGHT}px + env(safe-area-inset-right, 0px))`,
    height: `min(480px, calc(100dvh - ${PANEL_BOTTOM + 20}px))`,
  };

  return (
    <>
      {/* ── Panel ── separado del botón para poder posicionarlo independientemente */}
      {isOpen && (
        <div
          ref={panelRef}
          className="fixed z-50 flex flex-col overflow-hidden bg-white border border-violet-100 rounded-2xl shadow-2xl left-3 right-3 sm:left-auto sm:w-96"
          style={panelStyle}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-violet-600 to-violet-700 px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <div>
                <p className="text-white font-semibold text-sm leading-tight">Asistente Técnico</p>
                <p className="text-white/60 text-xs leading-tight">IA · Climatización y aerotermia</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <button onClick={reset} title="Nueva conversación"
                  className="text-white/60 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              )}
              <button onClick={() => setIsOpen(false)} title="Minimizar"
                className="text-white/60 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Mensajes */}
          <div className="flex-1 overflow-y-auto bg-violet-50 p-3 space-y-2.5">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full gap-4 py-4">
                <p className="text-xs text-gray-500 text-center px-2">
                  Pregunta sobre materiales, instalaciones o procedimientos técnicos
                </p>
                <div className="w-full space-y-1.5">
                  {SUGGESTIONS.map((s, i) => (
                    <button key={i} onClick={() => send(s)}
                      className="w-full text-left text-xs px-3 py-2.5 bg-white border border-violet-200 rounded-xl text-gray-600 hover:bg-violet-50 hover:border-violet-400 transition-colors active:scale-[0.98]">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => <ChatBubble key={i} msg={msg} />)}

            {loading && (
              <div className="flex items-end gap-2">
                <div className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div className="bg-white border border-violet-100 rounded-2xl rounded-bl-sm px-3 py-2 shadow-sm">
                  <div className="flex gap-1 items-center h-3">
                    <span className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            {error && (
              <p className="text-center text-xs text-red-500 bg-red-50 rounded-xl px-3 py-2">{error}</p>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Aviso de conversación larga */}
          {messages.length >= 12 && (
            <div className="bg-amber-50 border-t border-amber-200 px-3 py-2 flex items-center justify-between gap-2 flex-shrink-0">
              <span className="text-xs text-amber-700">Conversación larga — inicia una nueva para reducir coste</span>
              <button
                onClick={reset}
                className="text-xs font-semibold text-amber-800 hover:text-amber-900 underline flex-shrink-0"
              >
                Nueva
              </button>
            </div>
          )}

          {/* Input */}
          <div className="p-2.5 bg-white border-t border-gray-100 flex gap-2 flex-shrink-0">
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              rows={1}
              placeholder="Escribe tu consulta..."
              disabled={loading}
              className="flex-1 resize-none px-3 py-2 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 disabled:opacity-50"
              style={{ minHeight: 40, maxHeight: 80 }}
            />
            <button
              onClick={() => send()}
              disabled={!input.trim() || loading}
              className="px-3 py-2 bg-violet-600 hover:bg-violet-700 active:bg-violet-800 disabled:opacity-40 text-white rounded-xl transition-colors flex-shrink-0"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* ── Botón burbuja ── */}
      <button
        onClick={() => setIsOpen(v => !v)}
        className="fixed z-50 w-14 h-14 bg-violet-600 hover:bg-violet-700 active:bg-violet-800 text-white rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center"
        style={btnStyle}
        title="Asistente Técnico IA"
      >
        {isOpen ? (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        ) : (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        )}
        {unread && !isOpen && (
          <span className="absolute top-0.5 right-0.5 w-3 h-3 bg-red-500 rounded-full border-2 border-white" />
        )}
      </button>
    </>
  );
}
